import React, { useEffect, useState } from "react";
import axios from "axios";
import Paper from "@mui/material/Paper";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Loader from "./Loader";
import { toast } from "react-toastify";
import {
  Button,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import "react-toastify/dist/ReactToastify.css";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { IconButton, Tooltip } from "@mui/material";
import { Modal, Form } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";

const Messages = () => {
  const [rows, setRows] = useState([]);
  const [ogrows, setOGRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Add modal form states
  const [newMessageText, setNewMessageText] = useState("");
  const [newMessageStatus, setNewMessageStatus] = useState(true);

  // Validation schema for edit modal
  const validationSchema = Yup.object({
    message_text: Yup.string()
      .trim()
      .required("Message is required")
      .min(1, "Message cannot be empty"),
  });

  // Fetch messages on component mount
  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/getMessageList/admin/`,
        {
          security_key: "C3RL@Pl[LsRfmqJ63_",
          data: {
            user: "admin",
          },
        }
      );

      // Handle both response structures
      const result = response.data?.message || response.message;

      if (result?.success) {
        const ResponseData = result.message;
        const messageData = ResponseData.map((item, index) => ({
          id: index + 1,
          message_id_pk: item.message_id_pk,
          message_text: item.message_text || "",
          status: item.status === 1 || item.status === "1",
          display_order: item.display_order || index + 1,
        }));
        setOGRows(messageData);
        setRows(messageData);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast.error("Failed to fetch messages", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle edit button click
  const handleEditClick = (row) => {
    setSelectedMessage(row);
    setShowEditModal(true);
  };

  // Handle edit submit
  const handleEditSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/updateMessage/admin/`,
        {
          security_key: "C3RL@Pl[LsRfmqJ63_",
          data: {
            user: "admin",
            message_id_pk: selectedMessage.message_id_pk,
            message_text: values.message_text.trim(),
          },
        }
      );

      // Handle both response structures
      const result = response.data?.message || response.message;

      if (result?.success) {
        // Check for duplicate error
        if (
          result?.message?.success === false &&
          result?.message?.error === "duplicate"
        ) {
          toast.error("This message already exists!", {
            position: "top-right",
            autoClose: 3000,
          });
        } else if (result?.message?.success === true || result?.message === 1) {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.message_id_pk === selectedMessage.message_id_pk
                ? { ...row, message_text: values.message_text.trim() }
                : row
            )
          );
          setOGRows((prevRows) =>
            prevRows.map((row) =>
              row.message_id_pk === selectedMessage.message_id_pk
                ? { ...row, message_text: values.message_text.trim() }
                : row
            )
          );
          setShowEditModal(false);
          setSelectedMessage(null);
          toast.success("Message updated successfully", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      } else {
        toast.error("Failed to update message", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error updating message:", error);
      toast.error("Failed to update message", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (row) => {
    try {
      const newStatus = row.status ? 0 : 1;
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/updateMessageStatus/admin/`,
        {
          security_key: "C3RL@Pl[LsRfmqJ63_",
          data: {
            user: "admin",
            message_id_pk: row.message_id_pk,
            status: newStatus,
          },
        }
      );

      // Handle both response structures
      const result = response.data?.message || response.message;

      if (result?.success && result?.message === 1) {
        setRows((prevRows) =>
          prevRows.map((r) =>
            r.message_id_pk === row.message_id_pk
              ? { ...r, status: newStatus === 1 }
              : r
          )
        );
        setOGRows((prevRows) =>
          prevRows.map((r) =>
            r.message_id_pk === row.message_id_pk
              ? { ...r, status: newStatus === 1 }
              : r
          )
        );
        toast.success(
          `Message ${
            newStatus === 1 ? "activated" : "deactivated"
          } successfully`,
          {
            position: "top-right",
            autoClose: 3000,
          }
        );
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  // Handle add new message
  const handleAddMessage = async () => {
    if (!newMessageText.trim()) {
      toast.warning("Please enter a message", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/addMessage/admin/`,
        {
          security_key: "C3RL@Pl[LsRfmqJ63_",
          data: {
            user: "admin",
            message_text: newMessageText.trim(),
            status: newMessageStatus ? 1 : 0,
          },
        }
      );

      // Handle both response structures
      const result = response.data?.message || response.message;

      if (result?.success) {
        // Check for duplicate error
        if (
          result?.message?.success === false &&
          result?.message?.error === "duplicate"
        ) {
          toast.error("This message already exists!", {
            position: "top-right",
            autoClose: 3000,
          });
        } else if (
          result?.message?.success === true ||
          result?.message?.message_id
        ) {
          const newId = result.message.message_id;
          const newRow = {
            id: rows.length + 1,
            message_id_pk: newId,
            message_text: newMessageText.trim(),
            status: newMessageStatus,
            display_order: rows.length + 1,
          };
          setRows((prevRows) => [...prevRows, newRow]);
          setOGRows((prevRows) => [...prevRows, newRow]);
          setShowAddModal(false);
          setNewMessageText("");
          setNewMessageStatus(true);
          toast.success("Message added successfully", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      } else {
        toast.error("Failed to add message", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error adding message:", error);
      toast.error("Failed to add message", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPage(0); // Reset to first page on search

    if (value.trim() === "") {
      setRows(ogrows);
    } else {
      const filtered = ogrows.filter((row) =>
        row.message_text.toLowerCase().includes(value.toLowerCase())
      );
      setRows(filtered);
    }
  };

  // Handle drag end for reordering
  const handleDragEnd = async (result) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return;
    }

    // Calculate actual indices based on pagination
    const actualSourceIndex = page * rowsPerPage + sourceIndex;
    const actualDestinationIndex = page * rowsPerPage + destinationIndex;

    // Reorder the rows array
    const reorderedRows = Array.from(rows);
    const [movedItem] = reorderedRows.splice(actualSourceIndex, 1);
    reorderedRows.splice(actualDestinationIndex, 0, movedItem);

    // Update display_order and id for all rows
    const updatedRows = reorderedRows.map((row, index) => ({
      ...row,
      id: index + 1,
      display_order: index + 1,
    }));

    // Update state immediately for smooth UX
    setRows(updatedRows);
    setOGRows(updatedRows);

    // Prepare order data for API
    const orderData = updatedRows.map((row) => ({
      message_id_pk: row.message_id_pk,
      display_order: row.display_order,
    }));

    // Send to API
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/updateMessageOrder/admin/`,
        {
          security_key: "C3RL@Pl[LsRfmqJ63_",
          data: {
            user: "admin",
            order_data: orderData,
          },
        }
      );

      const apiResult = response.data?.message || response.message;

      if (apiResult?.success) {
        toast.success("Order updated successfully", {
          position: "top-right",
          autoClose: 2000,
        });
      } else {
        // Revert on failure
        fetchMessages();
        toast.error("Failed to update order", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error updating order:", error);
      // Revert on failure
      fetchMessages();
      toast.error("Failed to update order", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = event.target.value;
    setRowsPerPage(value === "all" ? rows.length : parseInt(value, 10));
    setPage(0);
  };

  // Get current page rows
  const getCurrentPageRows = () => {
    if (rowsPerPage === -1 || rowsPerPage >= rows.length) {
      return rows;
    }
    return rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  // Styled components
  const StyledGridOverlay = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    "& .no-results-primary": {
      fill: "#3D4751",
      ...theme.applyStyles("light", {
        fill: "#AEB8C2",
      }),
    },
    "& .no-results-secondary": {
      fill: "#1D2126",
      ...theme.applyStyles("light", {
        fill: "#E8EAED",
      }),
    },
  }));

  function CustomNoResultsOverlay() {
    return (
      <StyledGridOverlay>
        <Box sx={{ mt: 2 }}>No matching data found.</Box>
      </StyledGridOverlay>
    );
  }

  // Check if drag is disabled (when searching)
  const isDragDisabled = searchTerm.trim() !== "";

  return (
    <Card sx={{ boxShadow: "none" }}>
      <style>
        {`
          p {
            margin-bottom: 0;
          }
          .drag-handle {
            cursor: grab;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #888;
          }
          .drag-handle:hover {
            color: #333;
          }
          .drag-handle.disabled {
            cursor: not-allowed;
            opacity: 0.3;
          }
          .dragging-row {
            background-color: #f5f5f5 !important;
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          }
          .MuiTableRow-root:hover {
            background-color: rgba(0, 0, 0, 0.04);
          }
        `}
      </style>

      {/* Add Message Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="formMessageText" className="mb-3">
            <Form.Label>Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter message"
              value={newMessageText}
              onChange={(e) => setNewMessageText(e.target.value)}
              autoFocus
            />
          </Form.Group>
          <Form.Group controlId="formMessageStatus">
            <FormControlLabel
              control={
                <Switch
                  checked={newMessageStatus}
                  onChange={(e) => setNewMessageStatus(e.target.checked)}
                  color="primary"
                />
              }
              label={newMessageStatus ? "Active" : "Inactive"}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outlined"
            style={{
              backgroundColor: "#fff",
              borderColor: "#000000",
              color: "#000000",
            }}
            onClick={() => setShowAddModal(false)}
            className="me-2"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: "#000000" }}
            onClick={handleAddMessage}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Message Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Message</Modal.Title>
        </Modal.Header>

        {selectedMessage && (
          <Formik
            initialValues={{ message_text: selectedMessage.message_text }}
            validationSchema={validationSchema}
            onSubmit={handleEditSubmit}
          >
            {({
              handleSubmit,
              handleChange,
              values,
              touched,
              errors,
              isSubmitting,
              handleBlur,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Modal.Body>
                  <Form.Group controlId="formEditMessage">
                    <Form.Label>Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="message_text"
                      placeholder="Enter message"
                      value={values.message_text}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.message_text && !!errors.message_text}
                      autoFocus
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.message_text}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="outlined"
                    style={{
                      backgroundColor: "#fff",
                      borderColor: "#000000",
                      color: "#000000",
                    }}
                    onClick={() => setShowEditModal(false)}
                    disabled={isSubmitting}
                    className="me-2"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    style={{ backgroundColor: "#000000" }}
                    type="submit"
                    disabled={isSubmitting}
                  >
                    Save
                  </Button>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        )}
      </Modal>

      <CardContent>
        <Typography variant="h6" gutterBottom>
          Messages
        </Typography>

        {/* Action Bar: Add Button and Search Box */}
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              style={{ backgroundColor: "#000000" }}
              startIcon={<AddIcon />}
              onClick={() => setShowAddModal(true)}
            >
              Add
            </Button>
          </Grid>

          <Grid item xs={12} md={10}>
            <TextField
              fullWidth
              size="small"
              name="searchTerm"
              value={searchTerm}
              placeholder="Search messages..."
              onChange={handleSearchChange}
            />
          </Grid>
        </Grid>

        {isDragDisabled && (
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mb: 1, display: "block" }}
          >
            * Drag and drop is disabled while searching
          </Typography>
        )}

        {loading && <Loader />}
        <Paper
          sx={{
            boxShadow:
              "0px 1px 0px -1px rgba(0, 0, 0, 0.2), 0px 1px 17px 2px rgba(0, 0, 0, 0.14), 0px 1px 3px -11px rgba(0, 0, 0, 0.12)",
            width: "100%",
          }}
        >
          <TableContainer>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Table sx={{ minWidth: 650 }} aria-label="messages table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 50 }}></TableCell>
                    <TableCell sx={{ width: 70 }}>S.No</TableCell>
                    <TableCell sx={{ width: 100 }}>Action</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell sx={{ width: 150 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <Droppable droppableId="messages-table">
                  {(provided) => (
                    <TableBody
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {getCurrentPageRows().length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <CustomNoResultsOverlay />
                          </TableCell>
                        </TableRow>
                      ) : (
                        getCurrentPageRows().map((row, index) => (
                          <Draggable
                            key={row.message_id_pk.toString()}
                            draggableId={row.message_id_pk.toString()}
                            index={index}
                            isDragDisabled={isDragDisabled}
                          >
                            {(provided, snapshot) => (
                              <TableRow
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={
                                  snapshot.isDragging ? "dragging-row" : ""
                                }
                                sx={{
                                  backgroundColor: snapshot.isDragging
                                    ? "#f5f5f5"
                                    : "inherit",
                                }}
                              >
                                <TableCell>
                                  <div
                                    {...provided.dragHandleProps}
                                    className={`drag-handle ${
                                      isDragDisabled ? "disabled" : ""
                                    }`}
                                  >
                                    <DragIndicatorIcon />
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {page * rowsPerPage + index + 1}
                                </TableCell>
                                <TableCell>
                                  <Tooltip title="Edit" placement="top" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditClick(row)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>{row.message_text}</TableCell>
                                <TableCell>
                                  <Switch
                                    checked={row.status}
                                    onChange={() => handleStatusToggle(row)}
                                    color="primary"
                                    inputProps={{
                                      "aria-label": "status switch",
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </Table>
            </DragDropContext>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 50, { value: -1, label: "All" }]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage === -1 ? rows.length : rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              "& .MuiTablePagination-input": {
                display: "inline-flex !important",
              },
            }}
          />
        </Paper>
      </CardContent>
    </Card>
  );
};

export default Messages;